import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    Alert,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    RadioGroup,
    FormControlLabel,
    Radio,
    TextField,
    Stack,
    Link as MUILink,
    Snackbar,
  } from '@mui/material';
import Counter from '../../components/common/Counter/Counter';
import PaginationTable from "../../components/common/PaginationTable/PaginationTable";
import PayoutListing from '../../components/common/PayoutListing/PayoutListing';
import Tabs from '../../components/common/Tabs/Tabs';
import PageSpinner from '../../components/common/PageSpinner/PageSpinner';
import { AiOutlineDollarCircle, AiOutlineCheckCircle, AiOutlineShoppingCart } from "react-icons/ai";
import { apiFetch } from '../../utils/api';
import styles from "./DashboardPayouts.module.css";
import * as Sentry from "@sentry/react";

const DashboardPayouts = () => {
    const [ isPageLoading, setIsPageLoading ] = useState(false);
    const [data, setData] = useState([]);
    const [balance, setBalance] = useState(0);
    const [totalSales, setTotalSales] = useState(0);
    const [totalPurchased, setTotalPurchased] = useState(0);
    const [activeTab, setActiveTab] = useState("All");
    const [sortColumn, setSortColumn] = useState(null);
    const [sortOrder, setSortOrder] = useState("asc");

    const [payoutAvailable, setPayoutAvailable] = useState(false);
    const [payoutAmount, setPayoutAmount] = useState(0);
    const [payoutDialogOpen, setPayoutDialogOpen] = useState(false);
    const [payoutMethod, setPayoutMethod] = useState("crypto"); // "crypto" | "paypal"
    const [paypalEmail, setPaypalEmail] = useState("");
    const [paypalEmailError, setPaypalEmailError] = useState("");
    const [cryptoTYOpen, setCryptoTYOpen] = useState(false);
    const [paypalTYOpen, setPaypalTYOpen] = useState(false);
    const [payPangeaUrl, setPayPangeaUrl] = useState("https://my.paypangea.com"); // fallback
    const [toastMsg, setToastMsg] = useState("");
    const [toastOpen, setToastOpen] = useState(false);

    const hasFetched = useRef(false);

    const fetchData = async () => {
        setIsPageLoading(true);
        try {
            const res = await apiFetch("/api/finances/user", {}, true);
            setData(res.finances || []);
            setBalance(res.balance || 0);
            setTotalSales(res.total_sales || 0);
            setTotalPurchased(res.total_purchases || 0);


            // ======= ADD HERE: read payout availability from backend response =======
            // Expecting your backend to include:
            //   payout_available: boolean
            //   payout_amount: number (in USD)
            //   paypangea_url: string (optional deep link)
            if (res.payout_available) {
                setPayoutAvailable(true);
            } else {
                setPayoutAvailable(false);
            }
            if (typeof res.payout_amount === "number") {
                setPayoutAmount(res.payout_amount);  
            } else {
                setPayoutAmount(0);  
            }      // =========================================================================
        } catch (err) {
            Sentry.captureException(err);
            console.error("❌ Failed to fetch finances:", err);
        } finally {
            setIsPageLoading(false);
        }
    };

    useEffect(() => {
        console.log(hasFetched)
        if (hasFetched.current) return;
        hasFetched.current = true;
        console.log(hasFetched)

        
        fetchData();
    }, []);

    const columns = [
        { label: "Name", key: "name", width: "22%" },
        { label: "Date", key: "date", width: "15%" },
        { label: "Type", key: "type", width: "15%" },
        { label: "Payment", key: "payment_type", width: "15%" },
        { label: "Amount", key: "amount", width: "15%" },
        { label: "Status", key: "status", width: "15%" },
    ];

    const handleTabChange = (tab) => {
        console.log(tab)
        setActiveTab(tab);
    };

    const filteredData = data.filter(item => {
        if (activeTab === "All") return true;
        return item.type.toLowerCase() === activeTab.toLowerCase();
    });

    const handlePageChange = (newPage, sortColumn, sortOrder) => {
        console.log(`Fetching page ${newPage} sorted by ${sortColumn} (${sortOrder})`);
    };

    const handleSortChange = (column, order) => {
        console.log(`Sorting by ${column} in ${order || 'none'} order`);
        setSortColumn(order ? column : null);
        setSortOrder(order || "asc");

        if (!order) {
            setData([...data]);
        } else {
            const sorted = [...data].sort((a, b) => {
                if (a[column] < b[column]) return order === "asc" ? -1 : 1;
                if (a[column] > b[column]) return order === "asc" ? 1 : -1;
                return 0;
            });
            setData(sorted);
        }
    };

    const openPayoutDialog = () => {
        setPayoutMethod("crypto");
        setPaypalEmail("");
        setPaypalEmailError("");
        setPayoutDialogOpen(true);
    };
    const closePayoutDialog = () => setPayoutDialogOpen(false);

    const handleRequestCrypto = async () => {
        try {
            // Optional: Create payout request on backend for audit/logging
            await apiFetch("/api/payouts/request", {
            method: "POST",
            body: JSON.stringify({ method: "crypto", amount: payoutAmount }),
            }, true);

            setPayoutDialogOpen(false);
            setCryptoTYOpen(true);
            fetchData();
        } catch (err) {
            Sentry.captureException(err);
            setToastMsg("Failed to create crypto payout. Please try again.");
            setToastOpen(true);
        }
    };

    const validateEmail = (email) => {
        // simple validation
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const handleRequestPaypal = async () => {
        if (!validateEmail(paypalEmail)) {
            setPaypalEmailError("Enter a valid PayPal email");
            return;
        }
        try {
            await apiFetch("/api/payouts/request", {
            method: "POST",
            body: JSON.stringify({ method: "paypal", email: paypalEmail, amount: payoutAmount }),
            }, true);

            setPayoutDialogOpen(false);
            setPaypalTYOpen(true);
            fetchData();
        } catch (err) {
            Sentry.captureException(err);
            setToastMsg("Failed to create PayPal payout. Please try again.");
            setToastOpen(true);
        }
    };
    
    if (isPageLoading) {
        return <PageSpinner />;
    }
    
    return (
        <Box className={styles.dashboardPayouts}>
            <Typography variant="h1" className={styles.header}>Finances</Typography>

            {payoutAvailable && (
                <Alert
                    severity="info"
                    className={styles.payoutAlert}
                    action={
                        <Button variant="contained" className={styles.primaryButton} onClick={openPayoutDialog}>
                        Withdraw
                        </Button>
                    }
                    >
                    A new payout is available: <strong>${payoutAmount.toLocaleString()}</strong>.
                </Alert>
            )}

            <Box className={styles.counters}>
                <Counter
                    icon={<AiOutlineDollarCircle />}
                    title="Your Balance"
                    number={`$${balance.toLocaleString()}`}
                />
                <Counter
                    icon={<AiOutlineCheckCircle />}
                    title="Total Sales"
                    number={`$${totalSales.toLocaleString()}`}
                />
                <Counter
                    icon={<AiOutlineShoppingCart />}
                    title="My Purchases"
                    number={`$${totalPurchased.toLocaleString()}`}
                />
            </Box>

            <Tabs tabs={["All", "Sale", "Purchase", "Payout"]} onTabChange={handleTabChange} />

            <PaginationTable
                data={filteredData}
                columns={columns}
                component={PayoutListing}
                onPageChange={handlePageChange}
                onSortChange={handleSortChange}
                sortColumn={sortColumn}
                sortOrder={sortOrder}
                itemsPerPage={10}
            />

            {/* Payout selection dialog */}
            <Dialog
            open={payoutDialogOpen}
            onClose={closePayoutDialog}
            fullWidth
            maxWidth="sm"
            PaperProps={{ className: styles.mmDialogPaper }}
            >
            <DialogTitle disableTypography className={styles.dialogTitleRow}>
                <Typography variant="h6" className={styles.dialogTitle}>Select payout method</Typography>
                <Typography variant="body2" className={styles.dialogSubtitle}>
                Amount to withdraw: <strong>${payoutAmount.toLocaleString()}</strong>
                </Typography>
            </DialogTitle>

            <DialogContent className={styles.dialogContent}>
                <Box className={styles.methodRow}>
                <Box
                    role="button"
                    tabIndex={0}
                    className={`${styles.methodCard} ${payoutMethod === "crypto" ? styles.selected : ""}`}
                    onClick={() => setPayoutMethod("crypto")}
                    onKeyDown={(e) => e.key === "Enter" && setPayoutMethod("crypto")}
                >
                    <Box className={styles.methodIcon}>
                    {/* wallet icon */}
                    <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true"><path d="M2 7a3 3 0 0 1 3-3h12a2 2 0 0 1 2 2v2h1a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-1v2a2 2 0 0 1-2 2H5a3 3 0 0 1-3-3V7zm17 10V7a1 1 0 0 0-1-1H5a2 2 0 0 0-2 2v10a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1zm3-6a1 1 0 0 0-1-1h-3v6h3a1 1 0 0 0 1-1v-4zm-4 2a1 1 0 1 0 0-2 1 1 0 0 0 0 2z" fill="currentColor"/></svg>
                    </Box>
                    <Box className={styles.methodText}>
                    <Typography className={styles.methodTitle}>Crypto (via PayPangea)</Typography>
                    <Typography className={styles.methodDesc}>On‑chain withdrawal within 24h.</Typography>
                    </Box>
                </Box>

                <Box
                    role="button"
                    tabIndex={0}
                    className={`${styles.methodCard} ${payoutMethod === "paypal" ? styles.selected : ""}`}
                    onClick={() => setPayoutMethod("paypal")}
                    onKeyDown={(e) => e.key === "Enter" && setPayoutMethod("paypal")}
                >
                    <Box className={styles.methodIcon}>
                    {/* paypal icon */}
                    <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true"><path d="M8.8 20.5H6.2a.8.8 0 0 1-.8-.9l1.5-9.6a.8.8 0 0 1 .8-.7h5.4c3.4 0 5.7 1.5 5.1 4.6-.5 2.7-2.6 4.1-5.6 4.1H9.8l-.5 2.5a.8.8 0 0 1-.8.7zm8-11.6c-.9-.1-1.9-.1-3-.1H8.1l.3-1.9c.1-.7.7-1.3 1.4-1.3h5.8c2.3 0 3.8.5 4.2 1.9.1.5.1 1-.1 1.4-.4-.1-1.1-.1-2-.1z" fill="currentColor"/></svg>
                    </Box>
                    <Box className={styles.methodText}>
                    <Typography className={styles.methodTitle}>PayPal</Typography>
                    <Typography className={styles.methodDesc}>Payout within 24 hours.</Typography>
                    </Box>
                </Box>
                </Box>

                {payoutMethod === "paypal" && (
                <TextField
                    label="PayPal Email"
                    value={paypalEmail}
                    onChange={(e) => {
                    setPaypalEmail(e.target.value);
                    if (paypalEmailError) setPaypalEmailError("");
                    }}
                    error={!!paypalEmailError}
                    helperText={paypalEmailError || "We’ll use this email to send your PayPal payout."}
                    fullWidth
                    className={styles.paypalInput}
                />
                )}
            </DialogContent>

            <DialogActions className={styles.dialogActions}>
                <Button onClick={closePayoutDialog} className={styles.textButton}>Cancel</Button>
                {payoutMethod === "crypto" ? (
                <Button variant="contained" onClick={handleRequestCrypto} className={styles.primaryButton}>
                    Continue
                </Button>
                ) : (
                <Button variant="contained" onClick={handleRequestPaypal} className={styles.primaryButton}>
                    Continue
                </Button>
                )}
            </DialogActions>
            </Dialog>

            {/* Crypto TY dialog */}
            <Dialog
            open={cryptoTYOpen}
            onClose={() => setCryptoTYOpen(false)}
            fullWidth
            maxWidth="sm"
            PaperProps={{ className: styles.mmDialogPaper }}
            >
            <DialogTitle disableTypography className={styles.dialogTitleRow}>
                <Typography variant="h6" className={styles.dialogTitle}>Crypto payout is scheduled</Typography>
            </DialogTitle>
            <DialogContent className={styles.tyContent}>
                <Typography className={styles.tyText}>
                It will arrive within 24 hours. 
                </Typography>
                <Typography className={styles.tyText}>
                We’ll send payout to your PayPangea wallet. Access it with the same email as you use on Memento Master.
                </Typography>
                <Button
                component="a"
                href={payPangeaUrl}
                target="_blank"
                rel="noopener"
                variant="contained"
                className={styles.primaryButton}
                >
                Open PayPangea
                </Button>
            </DialogContent>
            <DialogActions className={styles.dialogActions}>
                <Button variant="outlined" onClick={() => setCryptoTYOpen(false)} className={styles.outlinedButton}>
                Done
                </Button>
            </DialogActions>
            </Dialog>

            {/* PayPal TY dialog */}
            <Dialog
            open={paypalTYOpen}
            onClose={() => setPaypalTYOpen(false)}
            fullWidth
            maxWidth="sm"
            PaperProps={{ className: styles.mmDialogPaper }}
            >
            <DialogTitle disableTypography className={styles.dialogTitleRow}>
                <Typography variant="h6" className={styles.dialogTitle}>PayPal payout scheduled</Typography>

            </DialogTitle>
            <DialogContent className={styles.tyContent}>
                <Typography className={styles.tyText}>
                It will arrive within 24 hours. 
                </Typography>
                <Typography className={styles.tyText}>
                We’ll send payout to <strong>{paypalEmail}</strong>.
                </Typography>
            </DialogContent>
            <DialogActions className={styles.dialogActions}>
                <Button variant="contained" onClick={() => setPaypalTYOpen(false)} className={styles.primaryButton}>
                OK
                </Button>
            </DialogActions>
            </Dialog>

            {/* ======= ADD HERE: toast for errors ======= */}
            <Snackbar
                open={toastOpen}
                autoHideDuration={4000}
                onClose={() => setToastOpen(false)}
                message={toastMsg}
            />
            {/* ========================================= */}
        </Box>
    );
};

export default DashboardPayouts;